import { ConfirmModal } from "./ConfirmModal";
import { useCallback, useEffect, useState } from "react";

/** API pública (epico 8) - chaves de API da org + webhooks de eventos. */
interface ApiKeyRow {
  id: string;
  name: string;
  keyPrefix: string;
  scope: string[];
  expiresAt: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  revoked: boolean;
  /** Consumo mensal da chave (contador zera na virada do mês). */
  usage?: { period: string; calls: number } | null;
}

interface WebhookRow {
  id: string;
  url: string;
  events: string[];
  createdAt: string;
  lastDeliveryAt: string | null;
  lastDeliveryOk: boolean | null;
  lastError: string | null;
  lastReportAt: string | null;
}

/** Escopos oferecidos na criação de chave - precisam bater com API_KEY_SCOPES no backend. */
const API_SCOPES: { value: string; label: string }[] = [
  { value: "ask", label: "ask (POST /v1/ask)" },
  { value: "read", label: "read (GET /v1/conversations)" },
];

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
  } catch {
    return iso;
  }
}

interface Props {
  /** Sysadmin só - configura a org escolhida no seletor em vez da própria. */
  orgId?: string;
}

export function ApiPanel({ orgId }: Props) {
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [keyName, setKeyName] = useState("");
  const [keyScopes, setKeyScopes] = useState<string[]>(["ask", "read"]);
  const [keyBusy, setKeyBusy] = useState(false);
  // confirmações customizadas (nenhum diálogo nativo)
  const [confirmAction, setConfirmAction] = useState<{ kind: "rotate" | "revoke"; id: string } | null>(null);
  const [keyError, setKeyError] = useState<string | null>(null);
  const [newPlaintext, setNewPlaintext] = useState<string | null>(null);

  const [webhooks, setWebhooks] = useState<WebhookRow[]>([]);
  const [availableEvents, setAvailableEvents] = useState<string[]>([]);

  // rótulos pt-BR dos eventos de webhook (a chave crua é o contrato da API)
  const EVENT_PT: Record<string, string> = {
    "conversation.created": "Conversa criada",
    "budget.alert": "Alerta de orçamento",
    "usage.report": "Relatório periódico de uso",
    "invoice.issued": "Fatura emitida",
    "invoice.paid": "Fatura paga",
    "invoice.overdue": "Fatura vencida",
    "user.pending": "Usuário aguardando aprovação",
    "repo.sync_failed": "Sincronização de repo falhou",
    "doc.generated": "Documentação gerada",
  };
  const [whUrl, setWhUrl] = useState("");
  const [whEvents, setWhEvents] = useState<string[]>([]);
  const [whBusy, setWhBusy] = useState(false);
  const [whError, setWhError] = useState<string | null>(null);

  const qs = orgId ? `?orgId=${encodeURIComponent(orgId)}` : "";

  const loadKeys = useCallback(async () => {
    const r = await fetch(`/api/admin/apikeys${qs}`);
    if (r.ok) setKeys((await r.json()).items ?? []);
  }, [qs]);
  const loadWebhooks = useCallback(async () => {
    const r = await fetch(`/api/admin/webhooks${qs}`);
    if (r.ok) {
      const j = await r.json();
      setWebhooks(j.items ?? []);
      setAvailableEvents(j.availableEvents ?? []);
    }
  }, [qs]);

  useEffect(() => {
    void loadKeys();
    void loadWebhooks();
  }, [loadKeys, loadWebhooks]);

  const createKey = async () => {
    if (!keyName.trim()) return;
    if (keyScopes.length === 0) {
      setKeyError("Selecione ao menos um escopo.");
      return;
    }
    setKeyBusy(true);
    setKeyError(null);
    setNewPlaintext(null);
    try {
      const r = await fetch(`/api/admin/apikeys${qs}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: keyName.trim(), scope: keyScopes }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setKeyError(j.error ?? "Falha ao criar.");
        return;
      }
      setNewPlaintext(j.plaintext);
      setKeyName("");
      await loadKeys();
    } finally {
      setKeyBusy(false);
    }
  };

  const rotateKey = async (id: string) => {

    setKeyBusy(true);
    setKeyError(null);
    setNewPlaintext(null);
    try {
      const r = await fetch(`/api/admin/apikeys/${id}/rotate${qs}`, { method: "POST" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setKeyError(j.error ?? "Falha ao rotacionar.");
        return;
      }
      setNewPlaintext(j.plaintext);
      await loadKeys();
    } finally {
      setKeyBusy(false);
    }
  };

  const revokeKey = async (id: string) => {

    setKeyBusy(true);
    try {
      await fetch(`/api/admin/apikeys/${id}${qs}`, { method: "DELETE" });
      await loadKeys();
    } finally {
      setKeyBusy(false);
    }
  };

  const addWebhook = async () => {
    if (!whUrl.trim() || whEvents.length === 0) return;
    setWhBusy(true);
    setWhError(null);
    try {
      const r = await fetch(`/api/admin/webhooks${qs}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ url: whUrl.trim(), events: whEvents }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setWhError(j.error ?? "Falha ao cadastrar.");
        return;
      }
      setWhUrl("");
      setWhEvents([]);
      await loadWebhooks();
    } finally {
      setWhBusy(false);
    }
  };

  const removeWebhook = async (id: string) => {
    setWhBusy(true);
    try {
      await fetch(`/api/admin/webhooks/${id}${qs}`, { method: "DELETE" });
      await loadWebhooks();
    } finally {
      setWhBusy(false);
    }
  };

  const testWebhook = async (id: string) => {
    setWhBusy(true);
    try {
      await fetch(`/api/admin/webhooks/${id}/test${qs}`, { method: "POST" });
      await loadWebhooks();
    } finally {
      setWhBusy(false);
    }
  };

  return (
    <>
      <div className="admin-block">
        <h2>Chaves de API</h2>
        <p className="admin-user-meta" style={{ marginBottom: 10 }}>
          Autenticação Bearer pras rotas públicas de integração (<code>/v1</code>). O valor em claro só
          aparece uma vez, na criação ou rotação - guarde num lugar seguro.{" "}
          <a href="/integracao">Ver documentação (Swagger)</a>.
        </p>
        <div style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "flex-end", flexWrap: "wrap" }}>
          <label className="field" style={{ flex: "1 1 220px" }}>
            <span>Nome da chave</span>
            <input type="text" value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="Integração X" />
          </label>
          <button className="btn-approve" disabled={keyBusy} onClick={() => void createKey()} type="button">
            Criar chave
          </button>
        </div>
        <div style={{ display: "flex", gap: 14, marginBottom: 10 }}>
          {API_SCOPES.map((s) => (
            <label key={s.value} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
              <input
                type="checkbox"
                checked={keyScopes.includes(s.value)}
                onChange={(e) =>
                  setKeyScopes((prev) => (e.target.checked ? [...prev, s.value] : prev.filter((x) => x !== s.value)))
                }
              />
              {s.label}
            </label>
          ))}
        </div>
        {keyError && <div className="auth-error" style={{ marginBottom: 10 }}>{keyError}</div>}
        {newPlaintext && (
          <div className="admin-empty small" style={{ marginBottom: 10, wordBreak: "break-all", fontFamily: "monospace" }}>
            Copie agora - não vai aparecer de novo: <strong>{newPlaintext}</strong>
          </div>
        )}
        {keys.map((k) => (
          <div key={k.id} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
            <strong>{k.name}</strong>
            <code className="admin-user-meta">{k.keyPrefix}…</code>
            <span className={`badge ${k.revoked ? "warn" : "ok"}`}>{k.revoked ? "revogada" : "ativa"}</span>
            <span className="admin-user-meta">escopos: {k.scope.join(", ") || "—"}</span>
            <span className="admin-user-meta">último uso: {fmtDate(k.lastUsedAt)}</span>
            <span className="admin-user-meta">
              este mês: {k.usage?.calls ?? 0} chamada(s)
            </span>
            <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              <button className="btn-reject" disabled={keyBusy} onClick={() => setConfirmAction({ kind: "rotate", id: k.id })} type="button">
                Rotacionar
              </button>
              <button className="btn-reject" disabled={keyBusy} onClick={() => setConfirmAction({ kind: "revoke", id: k.id })} type="button">
                Revogar
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="admin-block">
        <h2>Webhooks de eventos</h2>
        <p className="admin-user-meta" style={{ marginBottom: 10 }}>
          Recebe um POST quando algo acontece nesta organização. Entrega com retry (até 3 tentativas).
        </p>
        <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label className="field" style={{ flex: "1 1 240px" }}>
            <span>URL</span>
            <input type="url" value={whUrl} onChange={(e) => setWhUrl(e.target.value)} placeholder="https://..." />
          </label>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", maxWidth: 640 }}>
            {availableEvents.map((ev) => (
              <label key={ev} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }} title={ev}>
                <input
                  type="checkbox"
                  checked={whEvents.includes(ev)}
                  onChange={(e) =>
                    setWhEvents((prev) => (e.target.checked ? [...prev, ev] : prev.filter((x) => x !== ev)))
                  }
                />
                {EVENT_PT[ev] ?? ev}
              </label>
            ))}
          </div>
          <button className="btn-approve" disabled={whBusy} onClick={() => void addWebhook()} type="button">
            Cadastrar
          </button>
        </div>
        {whError && <div className="auth-error" style={{ marginBottom: 10 }}>{whError}</div>}
        {webhooks.map((w) => (
          <div key={w.id} style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <code style={{ wordBreak: "break-all" }}>{w.url}</code>
              <span className="admin-user-meta">{w.events.map((ev) => EVENT_PT[ev] ?? ev).join(", ")}</span>
              {w.lastDeliveryAt && (
                <span className={`badge ${w.lastDeliveryOk ? "ok" : "warn"}`}>
                  {w.lastDeliveryOk ? "última entrega ok" : "última entrega falhou"}
                </span>
              )}
              {w.events.includes("usage.report") && (
                <span className="admin-user-meta">
                  {w.lastReportAt ? `último relatório: ${fmtDate(w.lastReportAt)}` : "primeiro relatório ainda não enviado"}
                </span>
              )}
              <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                <button className="btn-reject" disabled={whBusy} onClick={() => void testWebhook(w.id)} type="button">
                  Testar
                </button>
                <button className="btn-reject" disabled={whBusy} onClick={() => void removeWebhook(w.id)} type="button">
                  Remover
                </button>
              </div>
            </div>
            {w.lastError && (
              <p className="admin-user-meta" style={{ marginTop: 4, color: "var(--danger, #c0392b)" }}>
                {w.lastError}
              </p>
            )}
          </div>
        ))}
      </div>
      {confirmAction && (
        <ConfirmModal
          title={confirmAction.kind === "rotate" ? "Rotacionar chave" : "Revogar chave"}
          message={
            confirmAction.kind === "rotate"
              ? "Rotacionar esta chave? A chave atual para de funcionar imediatamente e o valor novo aparece uma única vez."
              : "Revogar esta chave? Qualquer integração usando ela para de funcionar."
          }
          confirmLabel={confirmAction.kind === "rotate" ? "Rotacionar" : "Revogar"}
          onConfirm={() => {
            if (confirmAction.kind === "rotate") void rotateKey(confirmAction.id);
            else void revokeKey(confirmAction.id);
          }}
          onClose={() => setConfirmAction(null)}
        />
      )}
    </>
  );
}
